(function () {
  class FurnitureColourPicker extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";

      this.colourInputs = Array.from(this.querySelectorAll("[data-fcp-colour-input]"));
      this.colourLabelValue = this.querySelector("[data-fcp-colour-value]");
      this.previewImage = this.querySelector("[data-fcp-preview-image]");
      this.previewFallback = this.querySelector("[data-fcp-preview-fallback]");
      this.variantIdInput = this.querySelector("[data-fcp-variant-id-input]");
      this.proxyRadio = this.querySelector("[data-fcp-proxy-radio]");

      this.variants = this.readVariants();
      this.colorIndex = Number(this.dataset.colorIndex || 0);
      this.productId = this.dataset.productId || "";

      this.bindEvents();
      this.syncFromCurrentState({ emitEvents: false });
    }

    readVariants() {
      const script = this.querySelector("[data-fcp-variants]");
      if (!script) return [];
      try {
        const parsed = JSON.parse(script.textContent || "[]");
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.error("Furniture colour picker: unable to parse variant data", error);
        return [];
      }
    }

    bindEvents() {
      this.colourInputs.forEach((input) => {
        input.addEventListener("change", () => {
          void this.syncFromCurrentState({ emitEvents: true });
        });
      });
    }

    getSelectedColourInput() {
      return this.colourInputs.find((input) => input.checked) || null;
    }

    async syncFromCurrentState({ emitEvents }) {
      const selectedColourInput = this.getSelectedColourInput();
      const selectedColour = selectedColourInput ? selectedColourInput.value : "";

      if (!selectedColour) return;

      // Prefer the variant pre-computed by Liquid on the chosen swatch input.
      const precomputedId = selectedColourInput
        ? Number(selectedColourInput.dataset.fcpVariantId || 0)
        : 0;

      let nextVariant = precomputedId
        ? this.variants.find((v) => v.id === precomputedId) || null
        : null;

      // Fall back to first available variant for this colour.
      if (!nextVariant || nextVariant.available === false) {
        const firstAvailable = this.findFirstAvailableVariantForColour(selectedColour);
        if (firstAvailable) {
          nextVariant = firstAvailable;
        }
        // If still nothing available, keep the precomputed (sold-out) variant so the
        // UI shows the right colour even if it can't be added to cart.
        if (!nextVariant) {
          nextVariant = precomputedId
            ? this.variants.find((v) => v.id === precomputedId) || null
            : null;
        }
      }

      this.updateUI(selectedColourInput, selectedColour, nextVariant);

      if (!nextVariant) return;

      this.updateVariantInputs(nextVariant.id);
      this.updateProxyVariantState(nextVariant);
      this.updateUrl(nextVariant.id);

      if (emitEvents) {
        await this.dispatchCompatibilityEvents(nextVariant);
      }
    }

    findFirstAvailableVariantForColour(selectedColour) {
      return (
        this.variants.find((variant) => {
          const options = variant.options || [];
          return options[this.colorIndex] === selectedColour && variant.available;
        }) || null
      );
    }

    updateUI(selectedColourInput, selectedColour, variant) {
      if (this.colourLabelValue) {
        this.colourLabelValue.textContent = selectedColour;
      }

      if (selectedColourInput) {
        const imageUrl = selectedColourInput.dataset.fcpSwatchImage || "";
        const hex = selectedColourInput.dataset.fcpHex || "#D9D9D9";

        if (this.previewImage) {
          if (imageUrl) {
            this.previewImage.src = imageUrl;
            this.previewImage.alt = selectedColour;
            this.previewImage.classList.remove("is-hidden");
          } else {
            this.previewImage.classList.add("is-hidden");
          }
        }

        if (this.previewFallback) {
          this.previewFallback.style.setProperty("--fcp-swatch-color", hex);
          if (imageUrl) {
            this.previewFallback.classList.add("is-hidden");
          } else {
            this.previewFallback.classList.remove("is-hidden");
          }
        }
      }

      this.updateUnavailableState();
    }

    updateUnavailableState() {
      this.colourInputs.forEach((input) => {
        const possible = this.variants.some((variant) => {
          const options = variant.options || [];
          return options[this.colorIndex] === input.value && variant.available;
        });
        input.disabled = !possible;
      });

      // If the currently checked input was just disabled, move to the first available.
      const selected = this.getSelectedColourInput();
      if (selected && selected.disabled) {
        const fallback = this.colourInputs.find((input) => !input.disabled);
        if (fallback) {
          fallback.checked = true;
          if (this.colourLabelValue) this.colourLabelValue.textContent = fallback.value;
        }
      }
    }

    updateVariantInputs(variantId) {
      if (this.variantIdInput) {
        this.variantIdInput.value = String(variantId);
      }

      const targetInputs = this.findMainVariantInputs();

      if (targetInputs.length) {
        targetInputs.forEach((input) => {
          if (!input) return;
          input.value = String(variantId);
          input.dispatchEvent(new Event("input", { bubbles: true }));
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      } else if (this.variantIdInput) {
        this.variantIdInput.dispatchEvent(new Event("input", { bubbles: true }));
        this.variantIdInput.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    findMainVariantInputs() {
      const inputs = [];
      const seen = new Set();

      const addInput = (candidate) => {
        if (!candidate || seen.has(candidate)) return;
        seen.add(candidate);
        inputs.push(candidate);
      };

      const isValidProductForm = (form) => {
        if (!form) return false;
        const id = typeof form.id === "string" ? form.id.toLowerCase() : "";
        if (id.includes("installment")) return false;
        return true;
      };

      const getVariantInput = (form) => {
        if (!isValidProductForm(form)) return null;
        return form.querySelector("select[name='id'], input[name='id']");
      };

      const nearestForm = this.closest("form[action^='/cart/add']");
      addInput(getVariantInput(nearestForm));

      const addToCartComponent = document.querySelector("add-to-cart-component");
      if (addToCartComponent) {
        addInput(getVariantInput(addToCartComponent.closest("form[action^='/cart/add']")));
      }

      const section = this.closest(".shopify-section");
      if (section) {
        section
          .querySelectorAll("form[action^='/cart/add']")
          .forEach((form) => addInput(getVariantInput(form)));
      }

      document
        .querySelectorAll("form[action^='/cart/add']")
        .forEach((form) => addInput(getVariantInput(form)));

      return inputs;
    }

    updateUrl(variantId) {
      if (!window.history || !window.location) return;
      const url = new URL(window.location.href);
      url.searchParams.set("variant", String(variantId));
      window.history.replaceState({}, "", url.toString());
    }

    updateProxyVariantState(variant) {
      if (!(this.proxyRadio instanceof HTMLInputElement) || !variant) return;

      this.proxyRadio.dataset.currentChecked = "false";
      this.proxyRadio.checked = false;
      this.proxyRadio.removeAttribute("checked");

      this.proxyRadio.checked = true;
      this.proxyRadio.setAttribute("checked", "checked");
      this.proxyRadio.dataset.variantId = String(variant.id);
      this.proxyRadio.dataset.optionAvailable = variant.available ? "true" : "false";
      this.proxyRadio.dataset.currentChecked = "true";

      this.proxyRadio.dispatchEvent(new Event("click", { bubbles: true }));
    }

    async dispatchCompatibilityEvents(variant) {
      const selectedDetail = {
        resource: {
          id: String(variant.id)
        }
      };

      this.dispatchEvent(
        new CustomEvent("variant:selected", {
          bubbles: true,
          detail: selectedDetail
        })
      );

      const updateHtml = await this.fetchVariantSectionHtml(variant.id);

      const updateDetail = {
        resource: {
          id: String(variant.id),
          available: Boolean(variant.available)
        },
        sourceId: String(variant.id),
        data: {
          html: updateHtml,
          productId: String(this.productId || ""),
          newProduct: null
        }
      };

      this.dispatchEvent(
        new CustomEvent("variant:update", {
          bubbles: true,
          detail: updateDetail
        })
      );

      this.dispatchEvent(
        new CustomEvent("variant:change", {
          bubbles: true,
          detail: {
            productId: this.productId,
            variantId: variant.id,
            available: variant.available,
            options: variant.options || []
          }
        })
      );

      document.dispatchEvent(
        new CustomEvent("variant:change", {
          detail: {
            productId: this.productId,
            variantId: variant.id,
            available: variant.available,
            options: variant.options || []
          }
        })
      );
    }

    async fetchVariantSectionHtml(variantId) {
      const sectionElement = this.closest(".shopify-section");
      const productFormComponent = this.closest("product-form-component");
      let sectionId =
        productFormComponent?.dataset?.sectionId ||
        sectionElement?.dataset?.sectionId ||
        sectionElement?.id ||
        "";

      if (sectionId.startsWith("shopify-section-")) {
        sectionId = sectionId.replace("shopify-section-", "");
      }

      if (!sectionId) return document;

      try {
        const url = new URL(window.location.href);
        url.search = "";
        url.searchParams.set("section_id", sectionId);
        url.searchParams.set("variant", String(variantId));

        const response = await fetch(url.toString(), {
          headers: { Accept: "text/html" }
        });
        if (!response.ok) return document;

        const htmlText = await response.text();
        return new DOMParser().parseFromString(htmlText, "text/html");
      } catch (error) {
        console.warn("Furniture colour picker: failed to fetch variant section HTML", error);
        return document;
      }
    }
  }

  if (!customElements.get("furniture-colour-picker")) {
    customElements.define("furniture-colour-picker", FurnitureColourPicker);
  }
})();
